/**
 * Dialog primitive — shadcn-style, utilitarian (D-03).
 * Uses React portals; no external dependency.
 */
import * as React from 'react';
import { createPortal } from 'react-dom';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children?: React.ReactNode;
  className?: string;
}

export function Dialog({ open, onClose, title, children, className = '' }: DialogProps) {
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'dialog-title' : undefined}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-scrim"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Panel */}
      <div
        className={[
          'relative z-10 bg-bg-card border border-border rounded-lg shadow-xl',
          'max-h-[90vh] overflow-y-auto w-full max-w-lg mx-4',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {title && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h2 id="dialog-title" className="text-sm font-semibold text-text">
              {title}
            </h2>
            <button
              onClick={onClose}
              className="rounded text-text-muted hover:text-text transition-colors p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-bg"
              aria-label="Close dialog"
            >
              ×
            </button>
          </div>
        )}
        <div className="px-4 py-3">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
